// constants/theme.js
//
// The VibeLink design tokens, taken from the reference templates in
// templates/ (see templates/README.md for which file maps to which page).
// This is the single source for colour, gradient, shadow, radius, type and
// motion values; COLORS (constants/colors.js), TYPOGRAPHY
// (constants/typography.js) and KAN101 (constants/mockClubsProfile.js) read
// from here so existing screens follow the templates without per-screen hex.
// Screens should use these tokens (or the components/ui primitives built on
// them), never literal hex / font names.

// ── Palette ──────────────────────────────────────────────────────────────────
export const PALETTE = Object.freeze({
  // Brand blues
  blue: "#2F6BFF",
  blueRoyal: "#1E5BFF",
  blueDeep: "#123FE0",
  blueBright: "#0089FF",
  blue700: "#1E4FD6",
  navy: "#051247",
  navyCard: "#0B1F4D",
  // Blue tints
  blue50: "#EEF3FF",
  blueSoft: "#EAF0FF",
  blue100: "#DCE7FF",
  blue200: "#B9CEFF",
  blue300: "#8FB0FF",
  linkOnDark: "#7FB0FF",
  // Accents
  green: "#22B573",
  greenDark: "#158A55",
  greenSoft: "#E7F8F0",
  greenBorder: "#BFEBD6",
  red: "#F5453B",
  redSoft: "#FDE8E6",
  amber: "#F5A623",
  amberSoft: "#FDEFD2",
  amberText: "#A35F00",
  purple: "#8B5CF6",
  purpleSoft: "#F0EAFE",
  purpleText: "#7442E6",
  // Neutrals
  ink: "#0B0B12",
  ink2: "#3A3C44",
  gray600: "#6B7280",
  gray400: "#9CA3AF",
  gray300: "#D1D5DB",
  gray250: "#D5DAE5",
  gray200: "#E5E8EF",
  track: "#E8EDF7",
  trackLight: "#EEF2FA",
  divider: "#EEF1F7",
  skeleton: "#D5DDEA", // loading placeholder: visible on screen #F3F6FC and white cards
  subRow: "#FAFBFE",
  wash: "#F1F4FA",
  screen: "#F3F6FC",
  white: "#FFFFFF",
  // Translucent whites (over photos / gradient fills)
  whiteFrost: "rgba(255,255,255,0.92)",
  whiteMuted: "rgba(255,255,255,0.85)",
  whiteGlass: "rgba(255,255,255,0.22)",
  whiteGlassBorder: "rgba(255,255,255,0.35)", // glass chip outline on blue (club profile)
  screenFrost: "rgba(243,246,252,0.92)", // PALETTE.screen at .92: translucent sticky-bar background
});

// ── Gradients (colors + 135deg-ish start/end for expo-linear-gradient) ──────
const DIAG = { start: { x: 0, y: 0 }, end: { x: 1, y: 1 } };
export const GRADIENTS = Object.freeze({
  // Header bands, segment pill, small icon circles: 135° #2F6BFF→#1E5BFF
  header: { colors: [PALETTE.blue, PALETTE.blueRoyal], ...DIAG },
  // CTAs, FAB, active controls, "You" avatar: 135° #2F6BFF→#123FE0
  cta: { colors: [PALETTE.blue, PALETTE.blueDeep], ...DIAG },
  // Wing: 150° #051247 → #1E5BFF 55% → #0089FF
  wing: { colors: [PALETTE.navy, PALETTE.blueRoyal, PALETTE.blueBright], locations: [0, 0.55, 1], start: { x: 0.1, y: 0 }, end: { x: 0.9, y: 1 } },
  // Dark promo / poster cards: 135° #0B1F4D→#123FE0
  dark: { colors: [PALETTE.navyCard, PALETTE.blueDeep], ...DIAG },
  // Success / connected: #22B573→#158A55
  success: { colors: [PALETTE.green, PALETTE.greenDark], ...DIAG },
  // App background wash: 180° #F5F8FF→#EAF0FE
  screen: { colors: ["#F5F8FF", "#EAF0FE"], start: { x: 0, y: 0 }, end: { x: 0, y: 1 } },
});

// ── Shadows (CSS box-shadow → RN shadow + Android elevation) ────────────────
const shadow = (color, y, blur, opacity, elevation) => ({
  shadowColor: color,
  shadowOffset: { width: 0, height: y },
  shadowOpacity: opacity,
  shadowRadius: blur, // the app has always used CSS blur as shadowRadius
  elevation,
});
export const SHADOWS = Object.freeze({
  xs: shadow("#101840", 1, 2, 0.06, 1),
  sm: shadow("#101840", 2, 8, 0.06, 2),
  card: shadow("#101840", 6, 20, 0.08, 4), // 0 6px 20px rgba(16,24,64,.08)
  lg: shadow("#101840", 12, 30, 0.12, 8), // 0 12px 30px rgba(16,24,64,.12)
  chipOnBlue: shadow(PALETTE.navy, 6, 16, 0.18, 4), // header chips
  pill: shadow(PALETTE.blue, 4, 12, 0.35, 4), // active segment
  button: shadow(PALETTE.blue, 6, 14, 0.32, 5), // Connect / small CTAs
  cta: shadow(PALETTE.blue, 10, 26, 0.42, 8), // RSVP / FAB
  fab: shadow(PALETTE.blue, 8, 24, 0.4, 8), // tab-bar centre button
  navy: shadow(PALETTE.navy, 14, 34, 0.35, 10), // posters, promo, toast
  sheet: shadow(PALETTE.navy, -20, 60, 0.3, 12), // bottom sheets
});

// ── Type (Archivo; weights 400–800) ─────────────────────────────────────────
export const FONTS = Object.freeze({
  regular: "Archivo_400Regular",
  medium: "Archivo_500Medium",
  semiBold: "Archivo_600SemiBold",
  bold: "Archivo_700Bold",
  extraBold: "Archivo_800ExtraBold",
});

// ── Shape ───────────────────────────────────────────────────────────────────
export const RADII = Object.freeze({
  stub: 7,
  segmentPill: 9,
  small: 10,
  md: 12,
  tile: 14,
  lg: 16,
  card: 18,
  cardLg: 20,
  hero: 22,
  sheet: 28,
  pill: 999,
});

// Text roles straight from the templates (size / family / line height / tracking).
export const TYPE = Object.freeze({
  poster: { fontFamily: FONTS.extraBold, fontSize: 40, lineHeight: 40, letterSpacing: -0.8 },
  pageTitle: { fontFamily: FONTS.bold, fontSize: 30, lineHeight: 32, letterSpacing: -0.6 },
  sheetTitle: { fontFamily: FONTS.extraBold, fontSize: 26, lineHeight: 30, letterSpacing: -0.5 },
  detailTitle: { fontFamily: FONTS.extraBold, fontSize: 24, lineHeight: 27 },
  screenTitle: { fontFamily: FONTS.extraBold, fontSize: 20, lineHeight: 24, letterSpacing: -0.2 },
  sectionTitle: { fontFamily: FONTS.bold, fontSize: 20, lineHeight: 24, letterSpacing: -0.2 },
  headerTitle: { fontFamily: FONTS.extraBold, fontSize: 18, lineHeight: 22 },
  blockTitle: { fontFamily: FONTS.extraBold, fontSize: 17, lineHeight: 21 },
  cardTitle: { fontFamily: FONTS.bold, fontSize: 15.5, lineHeight: 18 },
  rowTitle: { fontFamily: FONTS.bold, fontSize: 15, lineHeight: 18 },
  body: { fontFamily: FONTS.regular, fontSize: 14, lineHeight: 21 },
  segment: { fontFamily: FONTS.bold, fontSize: 13.5, lineHeight: 17 },
  meta: { fontFamily: FONTS.regular, fontSize: 12.5, lineHeight: 16 },
  metaStrong: { fontFamily: FONTS.semiBold, fontSize: 12.5, lineHeight: 16 },
  sectionLabel: { fontFamily: FONTS.bold, fontSize: 12, lineHeight: 15, letterSpacing: 1.2, textTransform: "uppercase" },
  caption: { fontFamily: FONTS.regular, fontSize: 11.5, lineHeight: 14 },
  tab: { fontFamily: FONTS.medium, fontSize: 11, lineHeight: 13 },
  badge: { fontFamily: FONTS.extraBold, fontSize: 10.5, lineHeight: 13 },
  button: { fontFamily: FONTS.extraBold, fontSize: 15, lineHeight: 18 },
  buttonSmall: { fontFamily: FONTS.bold, fontSize: 12.5, lineHeight: 15 },
});

// Confetti / celebration palette from the templates' success burst.
export const CELEBRATION_COLORS = Object.freeze([
  PALETTE.blue, PALETTE.blueBright, PALETTE.green, PALETTE.amber, PALETTE.purple, PALETTE.red,
]);
